import SplashLoading from 'features/common/ui/Loaders/SplashLoading'
import workspaceVectorUrl from 'assets/img/carto-logo-positive.svg'
import AccountCreationLoading from './AccountCreationLoading'
import { Outlet } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import useAuthenticationStatus, { AuthenticationStatus } from './useAuthenticationStatus'
import { ReactElement } from 'react'
import useComesAuthenticatedFromAccounts from './useComesAuthenticatedFromAccounts'
import { goToCompleteAccount } from './useIsAccountReady'
import { useUserAccountBasedFeatureFlags } from 'features/account/hooks/accountHooks'
import settings from 'settings'
import SnowflakeCustomOnBoarding from 'features/snowflake-in-app/ui/SnowflakeCustomOnBoarding'
import { useMeUser } from 'features/users/hooks/useMeUser'
import ErrorPage from 'features/errors/ui/ErrorPage'
import { isGuest } from 'features/users/model/user'

interface AuthenticatedRouteProps {
  element?: ReactElement
  prompt?: ReactElement
  allowGuestAccess?: boolean
}

function Entrypoint({ children }) {
  if (settings.isSnowflakeNativeApp) {
    return <SnowflakeCustomOnBoarding>{children || <Outlet />}</SnowflakeCustomOnBoarding>
  }

  return <>{children || <Outlet />}</>
}

function AuthenticatedRoute({ element, prompt, allowGuestAccess = false }: AuthenticatedRouteProps) {
  const { loginWithRedirect } = useAuth0()
  const user = useMeUser()
  const authenticationStatus = useAuthenticationStatus()
  const wasLoginSuccess = useComesAuthenticatedFromAccounts()

  useUserAccountBasedFeatureFlags()
  if (isGuest(user) && !allowGuestAccess) {
    return <ErrorPage code="404" />
  }

  if (authenticationStatus === AuthenticationStatus.Success) {
    return <Entrypoint children={element} />
  }

  if (authenticationStatus === AuthenticationStatus.NotAuthenticated) {
    if (prompt && !wasLoginSuccess) {
      return prompt
    } else {
      const returnTo = new URL(window.location.href).pathname

      // If `wasLoginSuccess` then go to `loginWithRedirect`, because we would be
      // logged in in accounts-www but we need to login in workspace-www too
      loginWithRedirect({ appState: { returnTo } })
      return <SplashLoading imageUrl={workspaceVectorUrl} />
    }
  }

  if (authenticationStatus === AuthenticationStatus.Loading) {
    return <SplashLoading imageUrl={workspaceVectorUrl} />
  }

  if (authenticationStatus === AuthenticationStatus.AccountProvisioning) {
    return <AccountCreationLoading />
  }

  if (authenticationStatus === AuthenticationStatus.Incomplete) {
    goToCompleteAccount()
  }

  return <>{element || <Outlet />}</>
}

export default AuthenticatedRoute
